/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * What someone sees when gym access they were using has just been taken away.
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogContent, DialogTitle } from "@lemonadetraining/web-ui";
import { useAcknowledgeNotice } from "../../hooks/useAcknowledgeNotice";
import { presentNotice, removedOutcomeLine } from "./accessNoticeCopy";
import type { StoredNotice } from "./accessNoticeCopy";

/*
 * Per-variant: a person can hold an unread demotion AND an unread removal at
 * once, in which case both surfaces mount together. One shared id would make
 * the modal's own label ambiguous and hand focus to whichever came first.
 */
const headingIDFor = (variant: "demoted" | "removed") => `access-${variant}-title`;

interface Props {
  notice: StoredNotice;
  /**
   * "demoted" replaces a Business Portal route the person can no longer open;
   * "removed" is the one-time modal shown wherever they happen to be.
   */
  variant: "demoted" | "removed";
  isBoxCoach: boolean;
}

const AccessRemovedPage: React.FC<Props> = ({ notice, variant, isBoxCoach }) => {
  const navigate = useNavigate();
  const acknowledge = useAcknowledgeNotice();

  // Focus the heading rather than announcing through a live region: a whole
  // page has replaced itself, and role="alert" would be too loud for that.
  // Found by id because DialogTitle does not forward a ref, and a dropped ref
  // would leave the reader stranded with no warning.
  const headingID = headingIDFor(variant);

  useEffect(() => {
    document.getElementById(headingID)?.focus();
  }, [headingID]);

  const presented = presentNotice(notice, { isBoxCoach });
  if (!presented) return null;

  const programmes = notice.programmes ?? [];

  if (variant === "removed") {
    // Only OK consumes the notice. Escape or a click outside dismisses the
    // dialog for now: this is the one message explaining the removal, and a
    // stray keypress must not spend it.
    const handleConfirm = () => {
      void acknowledge(notice.id);
      navigate("/dashboard/");
    };

    return (
      <Dialog open onOpenChange={() => {}}>
        {/* No close X: the only way past this is OK, which records that they
            were told. A visible control that does nothing is worse than none. */}
        <DialogContent
          showCloseButton={false}
          aria-labelledby={headingID}
          data-testid="access-removed-modal"
        >
          <DialogTitle id={headingID} tabIndex={-1} className="focus:outline-none">
            {presented.title}
          </DialogTitle>
          <div className="space-y-2 text-sm text-muted-foreground">
            {presented.body.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {programmes.length > 0 && (
              <ul className="list-disc pl-5" data-testid="access-removed-programmes">
                {programmes.map((programme) => (
                  <li key={programme.programmeUID ?? programme.name}>
                    {removedOutcomeLine(programme)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Button onClick={handleConfirm} data-testid="access-removed-ok">
            OK
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="mx-auto max-w-xl py-10" data-testid="access-removed-page">
      <h1
        id={headingID}
        tabIndex={-1}
        className="text-xl font-semibold text-foreground focus:outline-none"
      >
        {presented.title}
      </h1>
      <div className="mt-3 space-y-2 text-sm text-muted-foreground" aria-live="polite">
        {presented.body.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {isBoxCoach
            ? "This page is for admins only. Your coaching, programmes and classes are unchanged."
            : "The gym's admin pages are no longer available to you."}
        </p>
      </div>
      <Button
        className="mt-5"
        onClick={() => {
          void acknowledge(notice.id);
          navigate(isBoxCoach ? "/dashboard/coach-portal" : "/dashboard/");
        }}
        data-testid="access-removed-go-coach"
      >
        {isBoxCoach ? "Go to Coach Portal" : "Go to Dashboard"}
      </Button>
    </div>
  );
};

export default AccessRemovedPage;
